import addQueryString from '@Utils/addQueryString';
import booleanify from '@Utils/booleanify';
import qs from 'querystring';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Pagination as MuiPagination } from '@mui/material';
import Stack from '@mui/material/Stack';
import PaginationItem from '@mui/material/PaginationItem';
import styled from 'styled-components';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import moment from 'moment';
interface Props {
	totalPages: number;
	sortBy: any;
	onPageChangeRequest?(args: onPageChangeRequestMeta): void;
	refetch?: boolean;
}

const Pagination: React.FC<Props> = ({
	totalPages,
	onPageChangeRequest,
	sortBy,
	refetch,
}) => {
	const location = useLocation();
	const history = useHistory();
	const queryPage = parseInt(
		qs.parse(location.search.replace('?', '')).page?.toString() ?? ''
	);

	let initialPageNumber;
	if (!queryPage || queryPage <= 0) {
		initialPageNumber = 1;
	} else initialPageNumber = queryPage;

	/* states */
	const [activePage, setActivePage] = useState<number>(initialPageNumber);

	// Latest-ref: the request effect below must re-fire on REAL signals only
	// (URL, sort, refetch) — never on the callback's identity. Keying it on
	// the callback meant an inline-arrow caller re-armed it on every parent
	// render, looping requests forever (bundle analytics incident). Callers
	// that swap handlers conditionally must remount the table (key prop) or
	// use the `refetch` prop to trigger a reload.
	const onPageChangeRequestRef = useRef(onPageChangeRequest);
	onPageChangeRequestRef.current = onPageChangeRequest;

	const onPageChange = useCallback(
		(selected: number) => {
			setActivePage(selected);
			const search = addQueryString(location.search, 'page', selected);
			history.push({ search });
		},
		[history, location.search]
	);

	const structParams = useCallback(() => {
		const queries = qs.parse(location.search.replace('?', ''));
		const filters = Object.keys(queries)
			.filter((query) => /FILTER_/.test(query.toString()))
			.map((queryName) => {
				return {
					[queryName.replace('FILTER_', '')]: booleanify(
						queries[queryName]?.toString() ?? ''
					),
				};
			});

		const queryValue = queries['query'];
		const query = !!queryValue ? queryValue.toString() : '';
		// const startDate = queries["from"];
		const startDate = new Date(`${queries['from']}`);
		const perPage = queries['perPage'] ?? 10;
		const endDate = new Date(`${queries['to']}`);

		return { queries, filters, queryValue, query, startDate, perPage, endDate };
	}, [location.search]);

	const handlePageChange = useCallback(() => {
		const { endDate, filters, perPage, queries, query, queryValue, startDate } =
			structParams();
		onPageChangeRequestRef.current?.({
			activePage: queries.page ? parseInt(queries.page?.toString()) : 1,
			filters,
			// normalized: callers receive a {field, direction} object or
			// undefined — never null and never a fieldless {}.
			sortBy: sortBy?.field ? sortBy : undefined,
			endDate: endDate,
			startDate: startDate,
			perPage: Number(perPage),
			query,
		});
		if (!queryPage || queryPage <= 0) {
			initialPageNumber = 1;
		} else initialPageNumber = queryPage;
		setActivePage(initialPageNumber);
	}, [location.search, location.pathname, sortBy?.field, sortBy?.direction]);
	// location.pathname covers same-route param navigation (e.g. course 5 →
	// course 6 users), which previously refetched via the handler's identity
	// change when its useCallback deps included the route param.
	// sortBy is depended on by VALUE (field + direction), not identity, so
	// fresh-but-equal objects (e.g. from URL rehydration) never re-fire it.
	useEffect(() => {
		handlePageChange();
	}, [location.search, location.pathname, sortBy?.field, sortBy?.direction]);

	useEffect(() => {
		if (refetch) {
			handlePageChange();
		}
	}, [refetch]);
	return (
		<>
			<Stack spacing={2}>
				<StyledPagination
					count={totalPages}
					size="small"
					page={activePage}
					onChange={(e, value) => onPageChange(value)}
					color="primary"
					renderItem={(item) => (
						<PaginationItem
							sx={{
								fontSize: '11px',
							}}
							{...item}
						/>
					)}
				/>
			</Stack>
		</>
	);
};

export default React.memo(Pagination);

const StyledPagination = styled(MuiPagination)`
	.css-1col9cw-MuiButtonBase-root-MuiPaginationItem-root.Mui-selected {
		background-color: #5ac0fc;
	}
	.MuiButtonBase-root.MuiPaginationItem-root.Mui-selected {
		color: #ffffff;
		size: 11px;
		font-family: Roboto;
	}
	.css-4ih7pr-MuiButtonBase-root-MuiPaginationItem-root {
		font-family: Roboto;
	}
`;
